import Splide from '@splidejs/splide';
import { getShopifyInStockRecommendations } from '../services/recommendation.service';
import '../../styles/web-components/recommendations-carousel.scss';
import { DESKTOP_WIDTH, TABLET_WIDTH, isBelowDesktop } from '../constants/devices.constants';
import { withoutTrailingZeros, createSrcset } from '../helpers/utils';
import { preventAnimation } from '../services/accessibility.service';
const MAX_RECOMMENDATIONS = 6;
const MOBILE_SKELETON_SLIDE_COUNT = 3;
const CONTENT_FADE_MS = 250;
const DEBUG_SKELETON_HOLD_MS = 60000;
const DEBUG_SKELETON_PARAM = 'debugRecSkeleton';
const SLIDER_LOADING_CLASS = 'recommendations-carousel__slider--loading';
const SLIDER_FADING_OUT_CLASS = 'recommendations-carousel__slider--fading-out';
const SLIDER_PRE_FADE_IN_CLASS = 'recommendations-carousel__slider--pre-fade-in';
const defaultCarouselOptions = {
    mediaQuery: "min",
    gap: "12px",
    perPage: 2,
    arrows: true,
    pagination: false,
    padding: { right: "15%" },
    breakpoints: {
        [TABLET_WIDTH]: {
            perPage: 3
        },
        [DESKTOP_WIDTH]: {
            destroy: true
        }
    }
};
const template = `
  <h2 class="recommendations-carousel__title">${window.UNTUCKitConfig.inStockRecommendationTitle}</h2>
  <section class="recommendations-carousel__slider ${SLIDER_LOADING_CLASS}" aria-label="In-Stock Recommendations" aria-busy="true"></section>
`;
function renderSkeletonCard() {
    return `
    <div class="recommendations-carousel__skeleton-slide">
      <div class="recommendations-carousel__skeleton">
        <div class="recommendations-carousel__skeleton-image"></div>
        <div class="recommendations-carousel__skeleton-info">
          <div class="recommendations-carousel__skeleton-line recommendations-carousel__skeleton-line--name"></div>
          <div class="recommendations-carousel__skeleton-line recommendations-carousel__skeleton-line--name-secondary"></div>
          <div class="recommendations-carousel__skeleton-line recommendations-carousel__skeleton-line--price"></div>
          <div class="recommendations-carousel__skeleton-line recommendations-carousel__skeleton-line--in-stock"></div>
        </div>
      </div>
    </div>
  `;
}
function renderSkeletonTrack(count) {
    return `<div class="recommendations-carousel__skeleton-track" aria-hidden="true">${Array.from({ length: count }, renderSkeletonCard).join('')}</div>`;
}
function getSkeletonSlideCount() {
    return isBelowDesktop() ? MOBILE_SKELETON_SLIDE_COUNT : MAX_RECOMMENDATIONS;
}
function isDebugSkeletonMode() {
    return new URLSearchParams(window.location.search).has(DEBUG_SKELETON_PARAM)
        || sessionStorage.getItem(DEBUG_SKELETON_PARAM) === '1';
}
function logSkeletonLayout(phase, slider) {
    if (!isDebugSkeletonMode() || !slider)
        return;
    const slide = slider.querySelector('.recommendations-carousel__skeleton-slide, .splide__slide');
    const slideRect = slide?.getBoundingClientRect();
    console.info(`[recommendations-carousel:${phase}]`, {
        sliderWidth: slider.getBoundingClientRect().width,
        slideWidth: slideRect?.width,
        slideHeight: slideRect?.height,
        isLoading: slider.classList.contains(SLIDER_LOADING_CLASS),
    });
}
function waitForTransition(el, propertyName, fallbackMs) {
    return new Promise((resolve) => {
        const done = () => {
            el.removeEventListener('transitionend', onEnd);
            window.clearTimeout(timer);
            resolve();
        };
        const onEnd = (event) => {
            if (event.target === el && event.propertyName === propertyName) {
                done();
            }
        };
        el.addEventListener('transitionend', onEnd);
        const timer = window.setTimeout(done, fallbackMs + 50);
    });
}
function waitForStableWidth(el, minWidth = 100) {
    if (el.getBoundingClientRect().width >= minWidth) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        let timeout = undefined;
        const ro = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            if (width >= minWidth) {
                ro.disconnect();
                window.clearTimeout(timeout);
                resolve();
            }
        });
        ro.observe(el);
        timeout = window.setTimeout(() => {
            ro.disconnect();
            resolve();
        }, 1000);
    });
}
function waitForNextFrame() {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}
class RecommendationsCarousel extends HTMLElement {
    carousel;
    constructor() {
        super();
    }
    async connectedCallback() {
        this.innerHTML = template;
        const slider = this.getSliderEl();
        slider.innerHTML = renderSkeletonTrack(getSkeletonSlideCount());
        await waitForStableWidth(slider);
        logSkeletonLayout('skeleton-ready', slider);
        if (isDebugSkeletonMode()) {
            console.info(`[recommendations-carousel] Debug mode: skeleton held for ${DEBUG_SKELETON_HOLD_MS / 1000}s. ` +
                `Use ?${DEBUG_SKELETON_PARAM}=1 or sessionStorage.setItem("${DEBUG_SKELETON_PARAM}", "1")`);
            await new Promise((resolve) => window.setTimeout(resolve, DEBUG_SKELETON_HOLD_MS));
        }
        const size = this.getAttribute('size');
        const fit = this.getAttribute('fit');
        const gender = this.getAttribute('gender');
        try {
            let recItems = [];
            const productId = this.getAttribute('productId');
            const recs = await getShopifyInStockRecommendations(productId, size, fit);
            recItems = recs?.slice(0, MAX_RECOMMENDATIONS).filter((r) => r.tags.includes(`department:${gender}`)).map((r) => ({
                url: r.url,
                image_url: r.featured_image,
                name: r.title,
                sku: r.matched_variant_sku,
                sale: r.compare_at_price ? withoutTrailingZeros((r.price) / 100) : '',
                original_price: withoutTrailingZeros((r.compare_at_price || r.price) / 100),
                product_id: r.id,
                show_in_stock_message: r.matched_variant_sku ? true : false,
            }));
            if (!recItems?.length) {
                this.displayError();
                return;
            }
            const currencySymbol = window.UNTUCKitConfig.cartCurrencySymbol;
            const slideHtmlString = recItems.reduce((acc, rec) => {
                let recUrl = rec.url;
                if (rec.sku) {
                    recUrl = `${rec.url}${rec.url.includes('?') ? '&' : '?'}${`sku=${rec.sku}`}`;
                }
                const salePrice = `${currencySymbol}${rec.sale}`;
                const regularPrice = `${currencySymbol}${rec.original_price}`;
                const imageUrl = `${rec.image_url}${rec.image_url.includes('?') ? '&' : '?'}width=280`;
                const imageSrcset = createSrcset(rec.image_url, [280, 400, 600]);
                const htmlString = `
            <li class="splide__slide recommendations-carousel__slide">
              <a href="${recUrl}">
                <img class="recommendations-carousel__image" width="140" height="167" alt="${rec.name}" src="${imageUrl}" srcset="${imageSrcset}" sizes="(min-width: 1024px) 140px, (min-width: 768px) calc(28vw - 8px), calc(42.5vw - 6px)"/>
                <div class="recommendations-carousel__info">
                  <p class="recommendations-carousel__name">${rec.name}</p>
                  <p class="recommendations-carousel__price" data-product-id="${rec.product_id}">
                    <span class="recommendations-carousel__regular-price ${rec.sale ? 'recommendations-carousel__regular-price--sale' : ''}" aria-label="regular price ${regularPrice}">${regularPrice}</span>
                    <span class="recommendations-carousel__sale-price ${rec.sale ? '' : 'ADA__visually-hidden'}" aria-label="sale price ${salePrice}">${salePrice}</span>
                    ${rec.show_in_stock_message ? '<p class="recommendations-carousel__in-stock-message"><span class="inventory-level__indicator inventory-level__indicator--inStock" aria-hidden="true"></span>In Your Size</p>' : ''}
                  </p>
                </div>
              </a>
            </li>
          `;
                return acc + htmlString;
            }, '');
            await this.revealRecommendations(slideHtmlString);
        }
        catch (error) {
            console.error(error);
            this.displayError();
        }
    }
    disconnectedCallback() {
        this.destroyCarousel();
    }
    displayError(msg = "Sorry, we're unable to provide recommendations at the moment.") {
        this.destroyCarousel();
        const container = this.getSliderEl();
        if (container) {
            container.classList.remove(SLIDER_LOADING_CLASS, SLIDER_FADING_OUT_CLASS, SLIDER_PRE_FADE_IN_CLASS);
            container.innerHTML = `<p class="recommendations-carousel__error-message">${msg}</p>`;
            container.removeAttribute('aria-busy');
        }
    }
    getSliderEl() {
        return this.querySelector('.recommendations-carousel__slider');
    }
    async revealRecommendations(slideHtmlString) {
        const slider = this.getSliderEl();
        if (!preventAnimation()) {
            slider.classList.add(SLIDER_FADING_OUT_CLASS);
            await waitForTransition(slider, 'opacity', CONTENT_FADE_MS);
        }
        slider.classList.remove(SLIDER_LOADING_CLASS);
        slider.classList.add('splide');
        slider.innerHTML = `
      <div class="splide__track">
        <ul class="splide__list">${slideHtmlString}</ul>
      </div>
    `;
        this.mountCarousel();
        await waitForNextFrame();
        this.carousel?.refresh();
        slider.removeAttribute('aria-busy');
        logSkeletonLayout('loaded', slider);
        if (!preventAnimation()) {
            slider.classList.add(SLIDER_PRE_FADE_IN_CLASS);
            slider.classList.remove(SLIDER_FADING_OUT_CLASS);
            void slider.offsetHeight;
            slider.classList.remove(SLIDER_PRE_FADE_IN_CLASS);
        }
    }
    mountCarousel() {
        const root = this.querySelector('.splide');
        this.carousel = new Splide(root, defaultCarouselOptions).mount();
    }
    destroyCarousel() {
        if (this.carousel) {
            this.carousel.destroy();
            this.carousel = undefined;
        }
    }
}
if (!window.customElements.get("recommendations-carousel")) {
    customElements.define("recommendations-carousel", RecommendationsCarousel);
}
